import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Zap, TrendingUp, Target, Users, ChevronDown, ChevronUp } from "lucide-react";

// Google Script URL for form submissions
const SCRIPT_BASE = "https://script.google.com/macros/s/";
const SCRIPT_ID = "AKfycbzlTFZ3XPRjgSRNbnYsXJxJlEsJ6Z0b0vGsQIgJrTxofJb59COeLu-5RPFdndrRXPHUow";
const SCRIPT_SUFFIX = "/exec";
const GOOGLE_SCRIPT_URL = SCRIPT_BASE + SCRIPT_ID + SCRIPT_SUFFIX;

export default function SIBPage(): React.ReactElement {
  // Course waitlist form state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Own The Agent application state
  const [otaName, setOtaName] = useState("");
  const [otaEmail, setOtaEmail] = useState("");
  const [otaCompany, setOtaCompany] = useState("");
  const [otaCompanySize, setOtaCompanySize] = useState("");
  const [otaIndustry, setOtaIndustry] = useState("");
  const [otaInterest, setOtaInterest] = useState("");
  const [otaStage, setOtaStage] = useState("");
  const [otaGoals, setOtaGoals] = useState("");
  const [otaSubmitting, setOtaSubmitting] = useState(false);
  const [otaSubmitted, setOtaSubmitted] = useState(false);

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  async function handleWaitlistSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      email: formData.get("email"),
      firstName: formData.get("firstName"),
      company: formData.get("company"),
      industry: formData.get("industry"),
      timestamp: new Date().toISOString(),
      source: "SIB Landing Page - AI Fundamentals Waitlist",
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      setSubmitted(true);
      e.currentTarget.reset();
      setEmail("");
      setFirstName("");
      setCompany("");
      setIndustry("");
    } catch (error) {
      console.error("Failed to submit waitlist form:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleOtaSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setOtaSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      company: formData.get("company"),
      companySize: formData.get("companySize"),
      industry: formData.get("industry"),
      interest: formData.get("interest"),
      stage: formData.get("stage"),
      goals: formData.get("goals"),
      timestamp: new Date().toISOString(),
      source: "SIB Landing Page - Own The Agent Application",
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      setOtaSubmitted(true);
      e.currentTarget.reset();
      setOtaName("");
      setOtaEmail("");
      setOtaCompany("");
      setOtaCompanySize("");
      setOtaIndustry("");
      setOtaInterest("");
      setOtaStage("");
      setOtaGoals("");
    } catch (error) {
      console.error("Failed to submit OTA application:", error);
    } finally {
      setOtaSubmitting(false);
    }
  }

  const faqs = [
    {
      question: "Is this just another AI course?",
      answer: "No. Most AI courses teach you which tools to use. SIB teaches you how to build systems that improve themselves. Tools change every month. Frameworks compound over years."
    },
    {
      question: "Do I need technical skills?",
      answer: "No. The framework is designed for business owners and operators, not engineers. You need business judgment, not coding skills."
    },
    {
      question: "How is this different from traditional process improvement?",
      answer: "Traditional process improvement is periodic and manual. Self-improving systems evaluate and optimize continuously, automatically. The improvement never stops."
    },
    {
      question: "What if I'm already using AI in my business?",
      answer: "Great start. SIB helps you move from \"using AI tools\" to \"building AI-augmented systems that improve themselves.\" Most businesses are still at the tools stage."
    },
    {
      question: "How long until I see results?",
      answer: "Quick wins in weeks (individual processes), compound advantage over months (integrated systems), transformational impact over years (true self-improving business)."
    },
    {
      question: "Is this only for tech companies?",
      answer: "No. The framework applies to any business with repeatable processes: professional services, construction, manufacturing, retail, healthcare, education. If you make decisions repeatedly, you can improve how those decisions are made."
    },
    {
      question: "What's the ROI?",
      answer: "Depends on implementation depth. Typical impacts: 20-40% time savings on automated processes, 10-30% quality improvements through evaluation loops, significant competitive advantage through compounding improvements. Real ROI is in decisions you can make that competitors can't."
    },
    {
      question: "What's the relationship between SIB and From Ads to AI?",
      answer: "From Ads to AI (FA2A) focuses on freelancers and agencies in the Google Ads space. Self-Improving Business focuses on business owners across all industries. Different audiences, compatible frameworks."
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Stop Using AI Tools.<br />Start Building a Self-Improving Business.
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90">
              While your competitors use AI to work faster, you'll build systems that teach themselves to improve. Every. Single. Day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8 py-6 bg-white text-purple-700 hover:bg-white/90" asChild>
                <a href="#course">Learn How It Works</a>
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10" asChild>
                <a href="#discovery">Book Discovery Call</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            You Have Two Choices
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-red-200 dark:border-red-800">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">
                  Choice 1: Wait and Hope
                </h3>
                <p className="text-lg mb-4">
                  Wait and hope your competitors don't use AI to get ahead.
                </p>
                <p className="text-sm text-muted-foreground italic">
                  (Hope is not a strategy.)
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-400 dark:border-green-600 ring-2 ring-green-400">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-green-600 dark:text-green-400">
                  Choice 2: Start Building Now
                </h3>
                <p className="text-lg">
                  Start building your self-improving business and compound your advantage every single day.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center space-y-4 text-lg">
            <p>
              Every day you wait, competitors who started building self-improving systems compound their advantage. They're not just working faster - they're building businesses that get smarter with every decision.
            </p>
            <p className="font-semibold text-xl">
              The businesses that start now will be unreachable in 12 months. The businesses that wait will spend years catching up.
            </p>
          </div>
        </div>
      </section>

      {/* What is a Self-Improving Business? */}
      <section className="bg-muted/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              The Framework That Changes Everything
            </h2>

            <div className="space-y-6 text-lg">
              <div>
                <p className="mb-4">
                  Most businesses ask: <span className="italic">"How do we use AI to work faster?"</span>
                </p>
                <p className="mb-4">
                  Self-improving businesses ask: <span className="font-bold">"How do we build systems that teach themselves to improve?"</span>
                </p>
                <p className="font-semibold">The difference is profound.</p>
              </div>

              <Card className="border-2 border-primary">
                <CardContent className="p-6">
                  <p className="text-lg">
                    When you automate a task, you save time once. When you build a self-improving system, it gets better every time it runs.
                  </p>
                </CardContent>
              </Card>

              <div>
                <p className="mb-4">
                  Knowledge compounds. Processes optimize themselves. Your team teaches the system, and the system teaches your team.
                </p>
                <p>
                  This isn't about replacing people with AI. It's about building feedback loops that capture what works, identify what doesn't, and continuously refine both human and AI performance.
                </p>
              </div>

              <Card className="bg-primary/10 border-2 border-primary/30">
                <CardContent className="p-6">
                  <p className="text-lg font-semibold text-center">
                    It's about moving from "running your business" to "improving the system that runs your business."
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* The 10/80/10 Framework */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            From Delegation to Development
          </h2>

          <div className="mb-8">
            <p className="text-lg mb-6 text-center">
              The framework starts with 10/80/10, an evolution of traditional 80/20:
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-2 border-blue-300 dark:border-blue-700">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">10%</div>
                  <h3 className="font-bold text-lg mb-2">Setup</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Define the outcome</li>
                    <li>• Provide context</li>
                    <li>• Clarify success criteria</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-300 dark:border-purple-700">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">80%</div>
                  <h3 className="font-bold text-lg mb-2">Execution</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• The actual work</li>
                    <li>• Human or AI performs</li>
                    <li>• Results are delivered</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-300 dark:border-green-700 ring-2 ring-green-400">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">10%</div>
                  <h3 className="font-bold text-lg mb-2">Review & Improve</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Evaluate execution quality</li>
                    <li>• Assess process quality</li>
                    <li>• Implement improvements</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-green-50 dark:bg-green-900/20 border-2 border-green-400">
              <CardContent className="p-6">
                <p className="text-lg font-semibold mb-2">The Game-Changer:</p>
                <p>
                  That final 10% is what transforms delegation into development. It's where learning happens and improvement compounds.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Chains with Evaluation Points */}
      <section className="bg-muted/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Real Business Runs on Process Chains
            </h2>

            <p className="text-lg mb-8 text-center">
              Business processes aren't single tasks - they're chains of tasks. Between each task is an evaluation point that asks:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-2 border-primary">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">1. Execution Quality</h3>
                  <p>How well was the task completed?</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-primary">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">2. Process Quality</h3>
                  <p>How good was the process itself?</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-primary/10 border-2 border-primary/30">
              <CardContent className="p-6">
                <p className="text-lg">
                  Each evaluation point is an opportunity to improve. Over time, these improvements compound.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Human + AI Task Distribution */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Who Does What? (And When Does It Change?)
          </h2>

          <p className="text-lg mb-8 text-center">
            Not everything can or should be done by AI. The breakthrough is determining which tasks AI agents can handle autonomously, which require human judgment, and when to transition tasks from human to AI execution.
          </p>

          <h3 className="text-2xl font-bold mb-6 text-center">
            The Five Levers for AI Task Improvement
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Card className="border-2 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <h4 className="font-bold mb-2">1. Prompt</h4>
                <p className="text-sm">Is the instruction clear? Does it include necessary constraints?</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-200 dark:border-purple-800">
              <CardContent className="p-4">
                <h4 className="font-bold mb-2">2. Context</h4>
                <p className="text-sm">Does the agent have relevant background and examples?</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200 dark:border-green-800">
              <CardContent className="p-4">
                <h4 className="font-bold mb-2">3. Tools</h4>
                <p className="text-sm">Can it access the right capabilities?</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-yellow-200 dark:border-yellow-800">
              <CardContent className="p-4">
                <h4 className="font-bold mb-2">4. Resources</h4>
                <p className="text-sm">Does it have necessary documents and data?</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200 dark:border-red-800">
              <CardContent className="p-4">
                <h4 className="font-bold mb-2">5. Model</h4>
                <p className="text-sm">Is the AI model appropriate for this task?</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-primary/10 border-2 border-primary">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-2">LLM-as-Judge</h3>
              <p className="mb-4">
                An evaluator agent analyzes the task, outcome, and current levers, then suggests specific improvements.
              </p>
              <p className="mb-2">
                <span className="font-semibold">Currently:</span> Humans review and approve/improve/dismiss suggestions
              </p>
              <p>
                <span className="font-semibold">Future:</span> System becomes trusted enough to implement improvements autonomously
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* The Ask, Check, Trust Cycle */}
      <section className="bg-muted/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Building Trust Incrementally
            </h2>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-2 border-primary">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">1</div>
                  <h3 className="font-bold text-lg mb-2">Ask</h3>
                  <p>Give AI a task</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-primary">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">2</div>
                  <h3 className="font-bold text-lg mb-2">Check</h3>
                  <p>Verify the result</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-primary ring-2 ring-primary">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">3</div>
                  <h3 className="font-bold text-lg mb-2">Trust</h3>
                  <p>Trust grows with consistency</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-primary/10 border-2 border-primary/30">
              <CardContent className="p-6">
                <p className="text-lg text-center">
                  You ask bigger questions. The cycle repeats. Trust compounds.
                  <br /><br />
                  This is how you move from "AI assistant" to "AI teammate" to "AI that improves itself."
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who Not How */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Who Not How for the AI Age
          </h2>

          <p className="text-lg mb-8 text-center">
            Dan Sullivan's "Who Not How" philosophy asks: "Who can help me solve this problem?" rather than "How do I solve this problem?"
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-gray-300">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">Traditional</h3>
                <p className="text-sm">"How do I solve this?"</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-300 dark:border-blue-700">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">Sullivan</h3>
                <p className="text-sm">"Who can solve this?"</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-400 ring-2 ring-purple-400">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">SIB</h3>
                <p className="text-sm">"Should a human or AI solve this, and how do we improve whoever does it?"</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof / Authority */}
      <section className="bg-muted/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              From the Creator of Ads to AI
            </h2>
            <p className="text-lg mb-6 max-w-3xl mx-auto">
              Mike Rhodes has spent 20 years helping businesses leverage automation and AI in Google Ads, building tools used by thousands of agencies and advertisers worldwide.
            </p>
            <p className="text-lg max-w-3xl mx-auto">
              The Self-Improving Business framework extends those principles beyond advertising to entire businesses - systematically building AI-augmented operations that improve themselves.
            </p>
          </div>
        </div>
      </section>

      {/* AI Fundamentals Course Waitlist */}
      <section id="course" className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Start Your Journey: AI Fundamentals for Business Owners
            </h2>
            <p className="text-xl text-muted-foreground">
              A comprehensive ~90 minute course designed specifically for business owners who want to understand and implement AI in their organizations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-3">
                <Brain className="w-6 h-6 text-purple-600" />
                The Course Covers
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Why acting now compounds your competitive advantage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>The 10/80/10 framework for self-improving processes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Human vs AI task distribution decisions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>The five levers of AI task performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span>Building your first self-improving process</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-600" />
                Practical Implementation
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Process chains and evaluation points</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>LLM-as-Judge: automated quality control</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>The Ask, Check, Trust cycle</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Measuring what matters: ROI frameworks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Real-world examples from working businesses</span>
                </li>
              </ul>
            </div>
          </div>

          <Card className="border-2 border-primary">
            <CardContent className="p-8">
              <div className="bg-primary/10 p-4 rounded-lg mb-6 text-center">
                <p className="font-semibold mb-1">Course Status: In Development</p>
                <p className="text-muted-foreground">Launching Q1 2026</p>
              </div>

              <p className="text-lg font-semibold mb-4 text-center">
                Get free early access when it launches
              </p>

              {submitted ? (
                <div className="text-center py-8">
                  <div className="bg-green-100 dark:bg-green-900/30 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-green-600 dark:text-green-400 text-4xl">✓</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Welcome! You're on the list
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    You'll be the first to know when AI Fundamentals launches. In the meantime, consider booking a discovery call to identify quick wins specific to your business.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">
                    Submit Another Email
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email address *"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 text-base"
                  />
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="firstName"
                      type="text"
                      placeholder="First name *"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="h-12 text-base"
                    />
                    <Input
                      name="company"
                      type="text"
                      placeholder="Company name (optional)"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="h-12 text-base"
                    />
                  </div>
                  <select
                    name="industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-base"
                  >
                    <option value="">Industry (optional)</option>
                    <option value="professional-services">Professional Services</option>
                    <option value="construction">Construction</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="education">Education</option>
                    <option value="technology">Technology</option>
                    <option value="other">Other</option>
                  </select>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg py-6"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Get Free Early Access"}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    No spam, ever. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Discovery Call Offer */}
      <section id="discovery" className="relative py-16 bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              Ready to Identify Your First Self-Improving Process?
            </h2>

            <p className="text-xl text-center mb-8 text-white/90">
              Book a 25-minute discovery call to discuss how self-improving systems can transform your specific business
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-4">
                <Target className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Map Your Opportunities</h3>
                  <p className="text-sm text-white/90">Map your current processes to AI opportunities</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Brain className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Identify Quick Wins</h3>
                  <p className="text-sm text-white/90">Find quick wins and long-term strategy</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <TrendingUp className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Get Specific Steps</h3>
                  <p className="text-sm text-white/90">Walk away with actionable first steps</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Zap className="w-8 h-8 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Evaluate Fit</h3>
                  <p className="text-sm text-white/90">Determine if deeper engagement makes sense</p>
                </div>
              </div>
            </div>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 mb-6">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold mb-1">$450 AUD</p>
                  <p className="text-sm text-white/80">(€280 / $300 USD)</p>
                </div>
                <div className="text-sm text-white/90">
                  <h4 className="font-semibold mb-2">What Happens Next:</h4>
                  <ol className="space-y-1 list-decimal list-inside">
                    <li>Choose your time from available slots</li>
                    <li>Receive prep questionnaire (helps us make the call valuable)</li>
                    <li>25-minute focused strategy session</li>
                    <li>Walk away with specific next steps</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button size="lg" className="text-lg px-8 py-6 bg-white text-purple-700 hover:bg-white/90" asChild>
                <a href="#" onClick={(e) => e.preventDefault()}>
                  Schedule Your Discovery Call
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Own The Agent Mastermind */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-block bg-purple-100 dark:bg-purple-900/30 px-4 py-2 rounded-full text-sm font-semibold text-purple-900 dark:text-purple-200 mb-4">
              For Serious Implementers
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Own The Agent
            </h2>
            <p className="text-xl text-muted-foreground">
              Year-long mastermind for building self-improving systems across your entire organization
            </p>
          </div>

          <Card className="border-2 border-purple-500/30 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <p className="text-lg text-muted-foreground mb-2">Annual Investment</p>
                <p className="text-5xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                  $20,000 USD
                </p>
                <p className="text-sm text-muted-foreground">
                  Limited to 15 members
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg mb-8">
                <h3 className="font-bold text-lg mb-4 text-center">This is for operators who:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Are serious about building competitive moats through AI</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Want implementation support, not just theory</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>See AI transformation as strategic priority</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Are ready to move fast while others wait</span>
                  </li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    What You Get
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Deep implementation support for your specific business</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Regular strategic reviews and optimization sessions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Direct access to Mike Rhodes</span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    Plus
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Frameworks, templates, and playbooks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Peer learning with other advanced implementers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">•</span>
                      <span>Community of practice with others on the same journey</span>
                    </li>
                  </ul>
                </div>
              </div>

              {otaSubmitted ? (
                <div className="text-center py-8">
                  <div className="bg-green-100 dark:bg-green-900/30 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-green-600 dark:text-green-400 text-4xl">✓</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Application Received
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Thank you for your application. We'll review it and get back to you shortly.
                  </p>
                  <Button onClick={() => setOtaSubmitted(false)} variant="outline">
                    Submit Another Application
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleOtaSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="name"
                      type="text"
                      placeholder="Name *"
                      required
                      value={otaName}
                      onChange={(e) => setOtaName(e.target.value)}
                      className="h-12 text-base"
                    />
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email *"
                      required
                      value={otaEmail}
                      onChange={(e) => setOtaEmail(e.target.value)}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      name="company"
                      type="text"
                      placeholder="Company *"
                      required
                      value={otaCompany}
                      onChange={(e) => setOtaCompany(e.target.value)}
                      className="h-12 text-base"
                    />
                    <select
                      name="companySize"
                      value={otaCompanySize}
                      onChange={(e) => setOtaCompanySize(e.target.value)}
                      className="w-full h-12 px-3 rounded-md border border-input bg-background text-base"
                      required
                    >
                      <option value="">Company size *</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="500+">500+ employees</option>
                    </select>
                  </div>
                  <select
                    name="industry"
                    value={otaIndustry}
                    onChange={(e) => setOtaIndustry(e.target.value)}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-base"
                    required
                  >
                    <option value="">Industry *</option>
                    <option value="professional-services">Professional Services</option>
                    <option value="construction">Construction</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="education">Education</option>
                    <option value="technology">Technology</option>
                    <option value="other">Other</option>
                  </select>
                  <textarea
                    name="interest"
                    placeholder="Why are you interested in Own The Agent? *"
                    required
                    value={otaInterest}
                    onChange={(e) => setOtaInterest(e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-base"
                  />
                  <textarea
                    name="stage"
                    placeholder="What's your current AI implementation stage? *"
                    required
                    value={otaStage}
                    onChange={(e) => setOtaStage(e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-base"
                  />
                  <textarea
                    name="goals"
                    placeholder="What are you hoping to achieve in the next 12 months? *"
                    required
                    value={otaGoals}
                    onChange={(e) => setOtaGoals(e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-base"
                  />
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    disabled={otaSubmitting}
                  >
                    {otaSubmitting ? "Submitting..." : "Apply for Own The Agent"}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Selective application process · Limited availability
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Advisory Services */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block bg-slate-200 dark:bg-slate-700 px-4 py-2 rounded-full text-sm font-semibold text-slate-900 dark:text-slate-200 mb-4">
                Executive Level
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Strategic AI Advisory
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Board-level guidance for companies navigating AI transformation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <Target className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Strategy</h3>
                <p className="text-sm text-muted-foreground">
                  Build vs buy decisions and competitive positioning
                </p>
              </div>

              <div className="text-center">
                <Users className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Capability</h3>
                <p className="text-sm text-muted-foreground">
                  Team structure and skill development for AI-first organizations
                </p>
              </div>

              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-slate-600 dark:text-slate-400 mx-auto mb-4" />
                <h3 className="font-bold mb-2">Transformation</h3>
                <p className="text-sm text-muted-foreground">
                  Change management and organizational transformation
                </p>
              </div>
            </div>

            <Card className="border-2 border-slate-300 dark:border-slate-700">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">
                      Looking for board-level AI guidance?
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Beyond implementation, Mike is available for advisory board positions with companies navigating AI transformation.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <Button size="lg" className="text-lg px-8 py-6 bg-slate-700 hover:bg-slate-800" asChild>
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        Discuss Advisory Services
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Common Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-0">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <h3 className="font-bold text-lg pr-4">{faq.question}</h3>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-16 bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Start Building Your Self-Improving Business
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-white/90">
              The businesses that start building self-improving systems today will be unreachable in 12 months. The businesses that wait will spend years catching up.
            </p>
            <p className="text-2xl font-bold mb-12">
              Which will you be?
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
                <CardContent className="p-6 text-center">
                  <h3 className="font-bold text-lg mb-4">Course Waitlist</h3>
                  <p className="text-sm text-white/90 mb-6">Get free early access to AI Fundamentals</p>
                  <Button className="w-full bg-white text-purple-700 hover:bg-white/90" asChild>
                    <a href="#course">Get Free Access</a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all ring-2 ring-yellow-400">
                <CardContent className="p-6 text-center">
                  <h3 className="font-bold text-lg mb-4">Discovery Call</h3>
                  <p className="text-sm text-white/90 mb-6">25-minute strategy session</p>
                  <Button className="w-full bg-yellow-400 text-slate-900 hover:bg-yellow-300" asChild>
                    <a href="#discovery">Book Call - $450</a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all">
                <CardContent className="p-6 text-center">
                  <h3 className="font-bold text-lg mb-4">Own The Agent</h3>
                  <p className="text-sm text-white/90 mb-6">Year-long mastermind program</p>
                  <Button className="w-full bg-white text-purple-700 hover:bg-white/90" asChild>
                    <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.querySelector('section:has(form[onSubmit])').offsetTop - 100, behavior: 'smooth' }); }}>Apply Now</a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <p className="text-sm text-slate-400">
              © 2025 8020agent · Self-Improving Business Framework
            </p>
            <div className="flex justify-center gap-6 text-sm">
              <a href="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-slate-400 hover:text-white transition-colors">Terms of Service</a>
              <a href="/" className="text-slate-400 hover:text-white transition-colors">8020agent.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
